import { FeaturePageLayout } from "@/components/features/FeaturePageLayout";
import { FeatureHero } from "@/components/features/FeatureHero";
import { FeatureSection } from "@/components/features/FeatureSection";
import { FeatureCard } from "@/components/features/FeatureCard";
import { FeatureGrid } from "@/components/features/FeatureGrid";
import { HowItWorksSection } from "@/components/features/HowItWorksSection";
import { ScreenshotSection } from "@/components/features/ScreenshotSection";
import { RelatedFeaturesSection } from "@/components/features/RelatedFeaturesSection";
import { FeatureCTA } from "@/components/features/FeatureCTA";
import {
  Users,
  QrCode,
  Calendar,
  DollarSign,
  Clock,
  FileText,
  TrendingUp,
  UserCheck,
  Briefcase,
  Mail,
  BarChart3
} from "lucide-react";

export function WorkforcePage() {
  const features = [
    {
      icon: QrCode,
      title: "QR Time Tracking",
      description: "Employees clock in/out by scanning QR codes. No expensive hardware needed - just their phones."
    },
    {
      icon: Calendar,
      title: "Drag & Drop Scheduling",
      description: "Create and manage shifts with an intuitive visual calendar. Copy templates, handle conflicts, and plan weeks in minutes."
    },
    {
      icon: DollarSign,
      title: "Automated Payroll",
      description: "Calculate wages automatically for 25+ EU countries. Export to your payroll system with one click."
    },
    {
      icon: Users,
      title: "Employee Portal",
      description: "Give employees access to their schedules, time records, and payslips. Reduce admin questions."
    },
    {
      icon: FileText,
      title: "Contract Management",
      description: "Store and manage employee contracts, documents, and important information securely in one place."
    },
    {
      icon: TrendingUp,
      title: "Workforce Analytics",
      description: "Track attendance patterns, labor costs, and productivity. Make data-driven staffing decisions."
    }
  ];

  const howItWorksSteps = [
    {
      number: 1,
      title: "Add Employees",
      description: "Import or manually add your team with their roles and contract details",
      icon: Users
    },
    {
      number: 2,
      title: "Create Schedules",
      description: "Drag and drop shifts on the calendar or use templates for recurring patterns",
      icon: Calendar
    },
    {
      number: 3,
      title: "Track Time",
      description: "Employees scan QR codes to clock in/out, automatically recording hours worked",
      icon: QrCode
    },
    {
      number: 4,
      title: "Process Payroll",
      description: "Review hours, calculate wages, and export data for payment processing",
      icon: DollarSign
    }
  ];

  const relatedFeatures = [
    {
      title: "Recruitment & Hiring",
      description: "Find and onboard new team members seamlessly",
      icon: Briefcase,
      link: "/features/recruitment"
    },
    {
      title: "Marketing & Communications",
      description: "Keep your team informed with internal messaging",
      icon: Mail,
      link: "/features/marketing"
    },
    {
      title: "Analytics & Insights",
      description: "Analyze workforce performance and labor costs",
      icon: BarChart3,
      link: "/features/analytics"
    }
  ];

  return (
    <FeaturePageLayout>
      <FeatureHero
        title="Complete Workforce Management Solution"
        subtitle="From time tracking to payroll, manage your entire team with ease. QR-based clock in/out, drag-and-drop scheduling, and automated payroll for 25+ countries."
        primaryCTA={{ text: "Start Managing Your Team", link: "/sign-up" }}
        secondaryCTA={{ text: "See How It Works", link: "#how-it-works" }}
      />

      <FeatureSection
        title="Everything You Need to Manage Your Team"
        subtitle="Powerful tools for time tracking, scheduling, payroll, and employee management"
      >
        <FeatureGrid>
          {features.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </FeatureGrid>
      </FeatureSection>

      <FeatureSection
        title="How It Works"
        subtitle="Streamline your workforce management in 4 steps"
        gradient
      >
        <HowItWorksSection steps={howItWorksSteps} />
      </FeatureSection>

      <FeatureSection>
        <ScreenshotSection
          title="Scheduling Made Simple"
          description="Our visual calendar makes it easy to create and manage employee schedules. Drag and drop shifts, handle availability conflicts, copy templates for recurring schedules, and communicate changes instantly. Employees can view their schedules and submit requests directly from the employee portal."
        />
      </FeatureSection>

      <FeatureSection
        title="Related Features"
        subtitle="Build a complete HR and workforce solution"
        gradient
      >
        <RelatedFeaturesSection features={relatedFeatures} />
      </FeatureSection>

      <FeatureSection>
        <FeatureCTA
          title="Simplify How You Manage Your Workforce"
          description="Save hours every week on scheduling, time tracking, and payroll. Join businesses across Europe using Tag-Inn."
          primaryCTA={{ text: "Get Started Free", link: "/sign-up" }}
          secondaryCTA={{ text: "Book a Demo", link: "#contact" }}
        />
      </FeatureSection>
    </FeaturePageLayout>
  );
}
